/**
 * 经脉调息小游戏
 * 光点在圆环/条中往复移动，玩家在最佳区域点击
 */

export type BreathingResult = {
  efficiency: number;   // 3~8
  label: string;
};

export class BreathingGame {
  private overlay: HTMLElement | null = null;
  private onComplete: ((result: BreathingResult) => void) | null = null;
  private animationId: number = 0;
  private dotPosition: number = 0;     // 0~100
  private direction: number = 1;       // 1 or -1
  private speed: number = 1.5;         // 每帧移动百分比
  private isRunning: boolean = false;

  /** 打开小游戏 */
  start(callback: (result: BreathingResult) => void): void {
    this.onComplete = callback;
    this.dotPosition = 0;
    this.direction = 1;
    this.isRunning = true;

    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';
    this.overlay.innerHTML = `
      <div class="modal breathing-modal">
        <h2>⚡ 经脉调息</h2>
        <p style="color:var(--text-secondary);margin-bottom:16px;font-size:0.9em;">
          光点进入金色区域时点击「吸纳」按钮
        </p>
        <div class="breathing-bar">
          <div class="breathing-sweet" id="breathing-sweet"></div>
          <div class="breathing-dot" id="breathing-dot"></div>
        </div>
        <div style="display:flex;gap:12px;justify-content:center;margin-top:16px;">
          <button class="btn btn-primary" id="breathing-tap" style="font-size:1.1em;padding:10px 30px;">
            🌬️ 吸纳
          </button>
        </div>
        <div id="breathing-result" style="margin-top:12px;min-height:24px;color:var(--text-gold);"></div>
        <button class="btn btn-small" id="breathing-close" style="margin-top:12px;">关闭</button>
      </div>
    `;

    document.body.appendChild(this.overlay);

    document.getElementById('breathing-tap')?.addEventListener('click', () => this.onTap());
    document.getElementById('breathing-close')?.addEventListener('click', () => this.close(null));
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close(null);
    });

    this.animate();
  }

  private animate(): void {
    if (!this.isRunning || !this.overlay) return;

    // 光点移动
    this.dotPosition += this.direction * this.speed;
    if (this.dotPosition >= 100) {
      this.dotPosition = 100;
      this.direction = -1;
    } else if (this.dotPosition <= 0) {
      this.dotPosition = 0;
      this.direction = 1;
    }

    // 更新DOM
    const dot = this.overlay.querySelector('#breathing-dot') as HTMLElement;
    if (dot) dot.style.left = `${this.dotPosition}%`;

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  private onTap(): void {
    if (!this.isRunning) return;

    // 最佳区域：40%~60%之间
    const center = 50;
    const dist = Math.abs(this.dotPosition - center);

    let efficiency: number;
    let label: string;
    let color: string;

    if (dist <= 5) {
      efficiency = 8;
      label = '🔥 完美吐纳！';
      color = '#f90';
    } else if (dist <= 15) {
      efficiency = 5;
      label = '✨ 良好吐纳';
      color = '#fc0';
    } else if (dist <= 30) {
      efficiency = 3;
      label = '💨 普通吐纳';
      color = '#aaa';
    } else {
      efficiency = 1;
      label = '💤 走火入魔...';
      color = '#d44';
    }

    this.isRunning = false;
    cancelAnimationFrame(this.animationId);

    const resultEl = this.overlay?.querySelector('#breathing-result');
    if (resultEl) {
      resultEl.innerHTML = `<span style="color:${color};font-weight:bold;font-size:1.1em;">${label}</span> (×${efficiency})`;
    }

    // 禁用按钮
    const tapBtn = this.overlay?.querySelector('#breathing-tap') as HTMLButtonElement;
    if (tapBtn) tapBtn.disabled = true;

    setTimeout(() => this.close({ efficiency, label }), 1200);
  }

  private close(result: BreathingResult | null): void {
    this.isRunning = false;
    cancelAnimationFrame(this.animationId);
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    if (result && this.onComplete) {
      this.onComplete(result);
    }
  }
}
